import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarClock, Trash2, Plus } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function AdminSchedules() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [schema, setSchema] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "content", target_key: "", value: "", date: "", time: "" });

  const load = async () => {
    try {
      const [s, sc, p] = await Promise.all([
        api.get("/schedules"),
        api.get("/content/schema"),
        api.get("/products"),
      ]);
      setItems(s.data);
      setSchema(sc.data.schema);
      setProducts(p.data);
    } catch (err) {
      if (err?.response?.status === 401) nav("/admin/login");
    }
  };

  useEffect(() => {
    if (user === false) { nav("/admin/login"); return; }
    if (user) load();
  }, [user]);

  const contentKeys = schema.flatMap((p) => p.keys.filter((k) => k.type !== "image").map((k) => ({ ...k, page: p.page })));
  const imageKeys = schema.flatMap((p) => p.keys.filter((k) => k.type === "image").map((k) => ({ ...k, page: p.page })));
  const productKeys = products.flatMap((pr) => ["name", "tagline", "price", "ingredients"].map((f) => ({ key: `${pr.id}.${f}`, label: `${pr.name} — ${f}` })));

  const optionsForKind = form.kind === "content" ? contentKeys : form.kind === "image" ? imageKeys : productKeys;

  const create = async () => {
    if (!form.target_key || !form.value || !form.date || !form.time) return toast.error("Fill all fields");
    const publish_at = new Date(`${form.date}T${form.time}`).toISOString();
    try {
      await api.post("/schedules", { kind: form.kind, target_key: form.target_key, value: form.value, publish_at });
      toast.success("Scheduled");
      setOpen(false);
      setForm({ kind: "content", target_key: "", value: "", date: "", time: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this scheduled change?")) return;
    try {
      await api.delete(`/schedules/${id}`);
      toast.success("Cancelled");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="border-b border-black/10 bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-sm text-[#6B7280] hover:text-[#111] inline-flex items-center gap-1"><ArrowLeft size={14}/> Console</Link>
            <img src={LOGO_URL} alt="B-Raw" className="h-8 w-auto ml-3" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#D9381E] hover:bg-[#B02C15] rounded-full" data-testid="schedule-new"><Plus size={14} className="mr-2"/> New schedule</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display">Schedule a change</DialogTitle></DialogHeader>
              <div className="space-y-4" data-testid="schedule-form">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6B7280]">What to publish</Label>
                  <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, target_key: "" })} className="mt-1 w-full h-12 border border-black/10 rounded-md px-3 bg-white" data-testid="schedule-kind">
                    <option value="content">Text content</option>
                    <option value="image">Banner image (URL)</option>
                    <option value="product">Product field</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Which section</Label>
                  <select value={form.target_key} onChange={(e) => setForm({ ...form, target_key: e.target.value })} className="mt-1 w-full h-12 border border-black/10 rounded-md px-3 bg-white" data-testid="schedule-target">
                    <option value="">— pick —</option>
                    {optionsForKind.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6B7280]">New value {form.kind === "image" && "(image URL, e.g. /api/uploads/xxx.png)"}</Label>
                  <Textarea rows={form.kind === "content" ? 5 : 2} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1" data-testid="schedule-value" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Date</Label>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 h-12" data-testid="schedule-date" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Time</Label>
                    <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1 h-12" data-testid="schedule-time" />
                  </div>
                </div>
                <div className="text-xs text-[#6B7280]">Actual publish check runs every ~15 min. Aim for a time at least 15 min in the future.</div>
              </div>
              <DialogFooter>
                <Button onClick={create} className="bg-[#D9381E] hover:bg-[#B02C15]" data-testid="schedule-create">Schedule it</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-2">
          <CalendarClock className="text-[#D9381E]" />
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-[#111]">Scheduled Publishes</h1>
        </div>
        <p className="text-sm text-[#6B7280] mt-1">Queue any content, product edit or banner to auto-flip live at a future date. Great for Diwali, Holi and launch drops.</p>

        <div className="mt-6 bg-white border border-black/10 rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Publish at</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Value preview</TableHead>
                <TableHead className="text-right">Cancel</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="schedules-table-body">
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-[#6B7280]">No pending schedules. Click “New schedule” to add one.</TableCell></TableRow>
              ) : items.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="text-sm whitespace-nowrap">{new Date(s.publish_at).toLocaleString()}</TableCell>
                  <TableCell><Badge className="bg-[#111]">{s.kind}</Badge></TableCell>
                  <TableCell className="text-sm font-mono">{s.target_key}</TableCell>
                  <TableCell className="text-xs text-[#4B5563] max-w-[280px] truncate" title={s.value}>{s.value}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => cancel(s.id)} data-testid={`cancel-${s.id}`} className="text-[#D9381E]"><Trash2 size={14} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
